import type { EditEntityTarget } from "../../components/modals";
import type { ShadowLocation } from "../../types/location-map";

/**
 * Search params for location-management edit modals.
 * Mirrors dashboard location detail `tab` + `id`, adapted for the flat
 * locations page (`edit` names the entity kind).
 */
export const LOCATION_EDIT_PARAMS = {
	EDIT: "edit",
	ID: "id",
} as const;

const EDIT_TYPES = new Set(["floor", "location", "sensor", "zone"]);

const findLocationIdForZone = (
	locations: ShadowLocation[],
	zoneId: string,
): null | string => {
	for (const location of locations) {
		if ((location.zones ?? []).some((zone) => zone.id === zoneId)) {
			return location.id;
		}
	}
	return null;
};

const findLocationIdForFloor = (
	locations: ShadowLocation[],
	floorId: string,
): null | string => {
	for (const location of locations) {
		if ((location.floors ?? []).some((floor) => floor.id === floorId)) {
			return location.id;
		}
	}
	return null;
};

const findLocationIdForSensor = (
	locations: ShadowLocation[],
	sensorId: string,
): null | string => {
	for (const location of locations) {
		for (const zone of location.zones ?? []) {
			if ((zone.sensors ?? []).some((sensor) => sensor.id === sensorId)) {
				return location.id;
			}
		}
	}
	return null;
};

/** Read the active edit target from the current URL search params. */
export const parseLocationEditTarget = (
	searchParams: URLSearchParams,
	locations: ShadowLocation[],
): EditEntityTarget | null => {
	const edit = searchParams.get(LOCATION_EDIT_PARAMS.EDIT);
	const id = searchParams.get(LOCATION_EDIT_PARAMS.ID);
	if (!edit || !id || !EDIT_TYPES.has(edit)) return null;

	if (edit === "location") {
		if (!locations.some((location) => location.id === id)) return null;
		return { locationId: id, type: "location" };
	}

	if (edit === "zone") {
		const locationId = findLocationIdForZone(locations, id);
		if (!locationId) return null;
		return { locationId, type: "zone", zoneId: id };
	}

	if (edit === "floor") {
		const locationId = findLocationIdForFloor(locations, id);
		if (!locationId) return null;
		return { floorId: id, locationId, type: "floor" };
	}

	const locationId = findLocationIdForSensor(locations, id);
	if (!locationId) return null;
	return { locationId, sensorId: id, type: "sensor" };
};

/**
 * Apply or clear an edit target on a copy of the current search params.
 * Preserves unrelated params (e.g. future view filters).
 */
export const applyLocationEditTarget = (
	current: URLSearchParams,
	target: EditEntityTarget | null,
): URLSearchParams => {
	const next = new URLSearchParams(current);
	next.delete(LOCATION_EDIT_PARAMS.EDIT);
	next.delete(LOCATION_EDIT_PARAMS.ID);

	if (!target) return next;

	next.set(LOCATION_EDIT_PARAMS.EDIT, target.type);
	if (target.type === "location") {
		next.set(LOCATION_EDIT_PARAMS.ID, target.locationId);
	} else if (target.type === "zone") {
		next.set(LOCATION_EDIT_PARAMS.ID, target.zoneId);
	} else if (target.type === "floor") {
		next.set(LOCATION_EDIT_PARAMS.ID, target.floorId);
	} else {
		next.set(LOCATION_EDIT_PARAMS.ID, target.sensorId);
	}
	return next;
};
